import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

// Global UI-version preference (v1 = legacy pages, v2 = redesigned pages).
//
// This is the enabler for the incremental v2 migration: v2 pages ship alongside v1, the user
// flips this toggle, and the nav resolves menu links to the v2 route where one exists (falling
// back to v1 where a page has no v2 yet). Default is 'v1' so the toggle is a pure opt-in — nothing
// changes for existing users until they switch. Choice is persisted per browser.

const STORAGE_KEY = 'ui-version';
const VALID = new Set(['v1', 'v2']);

const readInitial = () => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return VALID.has(stored) ? stored : 'v1';
  } catch (e) {
    return 'v1';
  }
};

const UiVersionContext = createContext({ uiVersion: 'v1', setUiVersion: () => {}, toggleUiVersion: () => {} });

export function UiVersionProvider({ children }) {
  const [uiVersion, setUiVersionState] = useState(readInitial);

  const setUiVersion = useCallback((next) => {
    const value = VALID.has(next) ? next : 'v1';
    setUiVersionState(value);
    try {
      window.localStorage.setItem(STORAGE_KEY, value);
    } catch (e) {
      /* ignore persistence failure */
    }
  }, []);

  const toggleUiVersion = useCallback(() => {
    setUiVersion(uiVersion === 'v2' ? 'v1' : 'v2');
  }, [uiVersion, setUiVersion]);

  // Reflect on <html data-ui-version> so CSS can react if needed, and keep tabs loosely in sync.
  useEffect(() => {
    try {
      document.documentElement.setAttribute('data-ui-version', uiVersion);
    } catch (e) {
      /* no-op */
    }
  }, [uiVersion]);

  const value = useMemo(
    () => ({ uiVersion, setUiVersion, toggleUiVersion }),
    [uiVersion, setUiVersion, toggleUiVersion]
  );

  return <UiVersionContext.Provider value={value}>{children}</UiVersionContext.Provider>;
}

export const useUiVersion = () => useContext(UiVersionContext);

export default UiVersionContext;
